import {
  EnvironmentProviders,
  Injectable,
  Injector,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  Configuration,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { catchError, concat, filter, map, Observable, of, tap, throwError } from 'rxjs';
import { APP_ROUTE_URLS } from '../app-config/routing/app-route-paths';
import { AppConfig } from '../../shared/app-config';
import { IUser } from '../../shell/authorization/models/user.interface';
import { AUTH_PROVIDER, AuthProviderService, SupportedAuthProvider } from './auth-provider.interface';
import { AppConfigService } from '../../app-config.service';
import { getSupportedAuthProvider, shouldUseMicrosoftAuth } from './auth-config.utils';
import { isCockpitApiRequest } from './api-origin.utils';
import { FirebaseAuthService } from './firebase.service';
import { ZitadelAuthService } from './zitadel.service';

function msalInstanceFactory(): IPublicClientApplication {
  const configuration: Configuration = {
    auth: AppConfig.msal?.auth,
    cache: AppConfig.msal?.cache,
  };

  return new PublicClientApplication(configuration);
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: AppConfig.msal?.scopes || [],
    },
    loginFailedRoute: APP_ROUTE_URLS.login,
  };
}

const getAccountNameParts = (account: AccountInfo): { firstname: string; lastname: string } => {
  const displayName = account.name?.trim() || account.username;
  const [firstname, ...lastnameParts] = displayName.split(' ');

  return {
    firstname: firstname || account.username,
    lastname: lastnameParts.join(' '),
  };
};

/**
 * Maps a Microsoft account to the application user. No role is granted here: a signed-in Entra
 * account proves identity, not permission. The backend decides what the user may do, based on the
 * delegated scope and the Entra group membership carried by the access token.
 */
const mapAccountToUser = (account: AccountInfo): IUser => {
  const { firstname, lastname } = getAccountNameParts(account);

  return {
    firstname,
    lastname,
    email: account.username || '',
    roles: [],
    token: '',
    sessionIdentityKey: `${account.homeAccountId}|${account.tenantId}`,
  };
};

/**
 * Microsoft Entra ID authentication provider using MSAL Angular.
 * Handles redirect-based login/logout and token acquisition.
 */
@Injectable()
export class MicrosoftAuthService implements AuthProviderService {
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);

  currentUser(): IUser | null {
    let account = this._msalService.instance.getActiveAccount();
    const accounts = this._msalService.instance.getAllAccounts();

    if (!account && accounts.length > 0) {
      account = accounts[0];
      this._msalService.instance.setActiveAccount(account);
    }

    return account ? mapAccountToUser(account) : null;
  }

  sessionChanges$(): Observable<IUser | null> {
    const redirect$ = this._msalService.handleRedirectObservable().pipe(
      tap((result) => {
        if (result?.account) {
          this._msalService.instance.setActiveAccount(result.account);
        }
      }),
      map(() => this.currentUser())
    );

    const idle$ = this._msalBroadcastService.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      map(() => this.currentUser())
    );

    return concat(redirect$, idle$);
  }

  accessToken$(resourceUrl: string): Observable<string | null> {
    // Tokens are issued for the Cockpit API only. Any other destination gets no token, even if
    // something asks for one.
    if (!isCockpitApiRequest(resourceUrl)) {
      return of(null);
    }

    const account = this._msalService.instance.getActiveAccount();

    if (!account) {
      return of(null);
    }

    return this._msalService
      .acquireTokenSilent({
        account,
        scopes: AppConfig.msal?.scopes || [],
      })
      .pipe(
        map((result) => result.accessToken),
        catchError((error: unknown) => {
          if (error instanceof InteractionRequiredAuthError) {
            // The silent refresh needs the user. MSAL owns the redirect and its own in-progress
            // state, so this cannot turn into a redirect loop. The redirect is subscribed rather
            // than left dangling, so a rejected one (a sign-in already running in another tab, for
            // example) is observed instead of surfacing as an unhandled rejection.
            this._msalService
              .acquireTokenRedirect({ scopes: AppConfig.msal?.scopes || [], account })
              .subscribe({ error: () => undefined });

            // The caller is failed rather than left waiting. The browser normally navigates away
            // before this matters, but when the redirect does not happen the pending request
            // settles instead of hanging forever.
            return throwError(() => error);
          }

          // Anything else is a real token-acquisition failure and is reported as such, rather
          // than sending the request without credentials.
          return throwError(() => error);
        })
      );
  }

  login$(): Observable<string | undefined> {
    return this._msalService
      .loginRedirect({
        scopes: AppConfig.msal?.scopes || [],
      })
      .pipe(
        map(() => undefined),
        catchError(() => of('authentication.loginFailed'))
      );
  }

  logout$(): Observable<void> {
    return this._msalService
      .logoutRedirect({
        postLogoutRedirectUri: AppConfig.msal?.auth?.postLogoutRedirectUri ?? APP_ROUTE_URLS.login,
      })
      .pipe(map(() => undefined));
  }

  clearSession(): void {
    this._msalService.instance.setActiveAccount(null);
  }
}

@Injectable()
class MockAuthProviderService implements AuthProviderService {
  currentUser(): IUser | null {
    return null;
  }

  sessionChanges$(): Observable<IUser | null> {
    return of(null);
  }

  accessToken$(resourceUrl: string): Observable<string | null> {
    void resourceUrl;
    return of(null);
  }

  login$(): Observable<string | undefined> {
    return of('authentication.loginFailed');
  }

  logout$(): Observable<void> {
    return of(undefined);
  }

  clearSession(): void {}
}

@Injectable()
class RuntimeAuthProviderService implements AuthProviderService {
  private readonly _injector = inject(Injector);

  private _resolveAuthProvider(): AuthProviderService {
    switch (getSupportedAuthProvider()) {
      case SupportedAuthProvider.Microsoft:
        return shouldUseMicrosoftAuth()
          ? this._injector.get(MicrosoftAuthService)
          : this._injector.get(MockAuthProviderService);
      case SupportedAuthProvider.Firebase:
        return this._injector.get(FirebaseAuthService);
      case SupportedAuthProvider.Zitadel:
        return this._injector.get(ZitadelAuthService);
      case SupportedAuthProvider.Credentials:
      default:
        return this._injector.get(MockAuthProviderService);
    }
  }

  currentUser(): IUser | null {
    return this._resolveAuthProvider().currentUser();
  }

  sessionChanges$(): Observable<IUser | null> {
    return this._resolveAuthProvider().sessionChanges$();
  }

  accessToken$(resourceUrl: string): Observable<string | null> {
    return this._resolveAuthProvider().accessToken$(resourceUrl);
  }

  login$(): Observable<string | undefined> {
    return this._resolveAuthProvider().login$();
  }

  logout$(): Observable<void> {
    return this._resolveAuthProvider().logout$();
  }

  clearSession(): void {
    this._resolveAuthProvider().clearSession();
  }
}

/**
 * Provides the configured authentication provider to the application.
 * Include this provider in your app's providers array to enable credentials, Microsoft, Firebase, or Zitadel login.
 *
 * @example
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideAuth(),
 *     // ... other providers
 *   ]
 * });
 */
export function provideAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(async () => {
      await inject(AppConfigService).loadAppConfig();
    }),
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MicrosoftAuthService,
    FirebaseAuthService,
    ZitadelAuthService,
    MockAuthProviderService,
    RuntimeAuthProviderService,
    {
      provide: AUTH_PROVIDER,
      useExisting: RuntimeAuthProviderService,
    },
  ]);
}

export function provideMicrosoftAuth(): EnvironmentProviders {
  return provideAuth();
}
